/**
 * Structured, versioned illustrated-avatar configuration (client mirror of
 * backend `modules/users/avatar/avatar-config.ts` — keep the two in sync).
 *
 * opncue avatars are illustrated, never uploaded photos. Skin tone and head
 * covering are VISUAL options only: never race/ethnicity/religion, never used
 * for matching, ranking, filtering, or inference.
 */

export const AVATAR_VERSION = 1 as const;

export const AVATAR_SKIN_TONES = [
  "neutral_yellow",
  "tone_1",
  "tone_2",
  "tone_3",
  "tone_4",
  "tone_5",
  "tone_6",
  "tone_7",
  "tone_8",
] as const;

export const AVATAR_FACE_SHAPES = [
  "oval",
  "round",
  "square",
  "rectangle",
  "heart",
  "diamond",
  "triangle",
] as const;

export const AVATAR_EYE_STYLES = [
  "round",
  "almond",
  "wide",
  "narrow",
  "upturned",
  "sleepy",
] as const;

export const AVATAR_EYE_COLORS = [
  "brown",
  "dark_brown",
  "amber",
  "hazel",
  "green",
  "blue",
  "gray",
  "violet",
] as const;

export const AVATAR_BROW_STYLES = [
  "soft",
  "straight",
  "arched",
  "thick",
  "thin",
  "rounded",
] as const;

export const AVATAR_NOSE_STYLES = [
  "soft",
  "round",
  "narrow",
  "wide",
  "button",
] as const;

export const AVATAR_HAIR_STYLES = [
  "none",
  "buzz_cut",
  "cropped",
  "short_curls",
  "medium_curls",
  "wavy",
  "short_straight",
  "long",
  "bun",
  "short_locs",
  "box_braids",
  "long_locs",
  "fade",
  "side_part",
] as const;

export const AVATAR_HAIR_COLORS = [
  "jet_black",
  "dark_espresso",
  "chestnut_brown",
  "honey_blonde",
  "deep_auburn",
  "classic_ginger",
  "silver_gray",
  "off_white",
  "electric_blue",
  "hot_pink",
  "vibrant_violet",
  "sandy_blonde",
  "copper_red",
  "salt_pepper",
  "mint_green",
  "soft_lavender",
  "rose_gold",
  "teal_blue",
] as const;

export const AVATAR_FACIAL_HAIR = [
  "none",
  "stubble",
  "mustache",
  "beard_short",
  "beard_full",
  "goatee",
] as const;

export const AVATAR_GLASSES = ["none", "round", "square", "thin", "bold"] as const;

export const AVATAR_HEAD_COVERINGS = [
  "none",
  "beanie",
  "cap",
  "headscarf",
  "turban",
  "wrap",
] as const;

export const AVATAR_EXPRESSIONS = [
  "soft_smile",
  "smile",
  "calm",
  "cheerful",
  "neutral",
] as const;

export const AVATAR_TOP_STYLES = [
  "tee",
  "hoodie",
  "sweater",
  "jacket",
  "collared",
] as const;

export const AVATAR_COLORS = [
  "blue",
  "navy",
  "sky",
  "teal",
  "green",
  "amber",
  "coral",
  "pink",
  "purple",
  "slate",
] as const;

export const AVATAR_ACCESSORIES = [
  "none",
  "headphones",
  "earrings",
  "pin",
  "scarf",
] as const;

export const AVATAR_VIBE_ICONS = [
  "none",
  "coffee",
  "book",
  "music",
  "walk",
  "game",
  "art",
  "sport",
  "food",
  "volunteer",
  "study",
] as const;

/**
 * Profile pins — a badge the user deliberately chooses to show, covering
 * identity, access needs, and how they prefer to meet.
 *
 * The most sensitive values in this schema: several describe protected
 * characteristics (LGBTQ+ / trans identity) or health and disability. So:
 * opt-in only (default `none`, never assigned by seed generation or
 * Randomize), display only (never an input to matching, ranking, filtering or
 * inference — no filter may select people BY pin), and understood as the
 * user's own broadcast, since `avatarConfig` reaches nearby peers. See the
 * server mirror for the full rationale.
 */
export const AVATAR_PROFILE_PINS = [
  "none",
  "pride",
  "trans",
  "neurodivergent",
  "hidden_disability",
  "accessibility",
  "deaf_hoh",
  "service_animal",
  "mask_friendly",
  "alcohol_free",
  "alcohol_friendly",
  "cannabis_friendly",
  "no_smoking",
  "quiet_places",
  "low_sensory",
  "small_groups",
  "one_to_one",
  "text_first",
  "new_to_area",
] as const;

export type AvatarSkinTone = (typeof AVATAR_SKIN_TONES)[number];
export type AvatarFaceShape = (typeof AVATAR_FACE_SHAPES)[number];
export type AvatarEyeStyle = (typeof AVATAR_EYE_STYLES)[number];
export type AvatarEyeColor = (typeof AVATAR_EYE_COLORS)[number];
export type AvatarBrowStyle = (typeof AVATAR_BROW_STYLES)[number];
export type AvatarNoseStyle = (typeof AVATAR_NOSE_STYLES)[number];
export type AvatarHairStyle = (typeof AVATAR_HAIR_STYLES)[number];
export type AvatarHairColor = (typeof AVATAR_HAIR_COLORS)[number];
export type AvatarFacialHair = (typeof AVATAR_FACIAL_HAIR)[number];
export type AvatarGlasses = (typeof AVATAR_GLASSES)[number];
export type AvatarHeadCovering = (typeof AVATAR_HEAD_COVERINGS)[number];
export type AvatarExpression = (typeof AVATAR_EXPRESSIONS)[number];
export type AvatarTopStyle = (typeof AVATAR_TOP_STYLES)[number];
export type AvatarColor = (typeof AVATAR_COLORS)[number];
export type AvatarAccessory = (typeof AVATAR_ACCESSORIES)[number];
export type AvatarVibeIcon = (typeof AVATAR_VIBE_ICONS)[number];
export type AvatarProfilePin = (typeof AVATAR_PROFILE_PINS)[number];

const LEGACY_HAIR_STYLE_ALIASES: Record<string, AvatarHairStyle> = {
  short: "short_straight",
  curly_short: "short_curls",
  curly_medium: "medium_curls",
  locs_short: "short_locs",
  locs_long: "long_locs",
  braids: "box_braids",
  covered: "none",
};

export type UserAvatarConfig = {
  version: typeof AVATAR_VERSION;
  skinTone: AvatarSkinTone;
  faceShape: AvatarFaceShape;
  eyeStyle: AvatarEyeStyle;
  eyeColor: AvatarEyeColor;
  browStyle: AvatarBrowStyle;
  noseStyle: AvatarNoseStyle;
  hairStyle: AvatarHairStyle;
  hairColor: AvatarHairColor;
  facialHair: AvatarFacialHair;
  glasses: AvatarGlasses;
  headCovering: AvatarHeadCovering;
  expression: AvatarExpression;
  topStyle: AvatarTopStyle;
  topColor: AvatarColor;
  accessory: AvatarAccessory;
  vibeIcon: AvatarVibeIcon;
  profilePin: AvatarProfilePin;
};

export type AvatarField = keyof Omit<UserAvatarConfig, "version">;

const FIELD_OPTIONS: { [K in AvatarField]: readonly UserAvatarConfig[K][] } = {
  skinTone: AVATAR_SKIN_TONES,
  faceShape: AVATAR_FACE_SHAPES,
  eyeStyle: AVATAR_EYE_STYLES,
  eyeColor: AVATAR_EYE_COLORS,
  browStyle: AVATAR_BROW_STYLES,
  noseStyle: AVATAR_NOSE_STYLES,
  hairStyle: AVATAR_HAIR_STYLES,
  hairColor: AVATAR_HAIR_COLORS,
  facialHair: AVATAR_FACIAL_HAIR,
  glasses: AVATAR_GLASSES,
  headCovering: AVATAR_HEAD_COVERINGS,
  expression: AVATAR_EXPRESSIONS,
  topStyle: AVATAR_TOP_STYLES,
  topColor: AVATAR_COLORS,
  accessory: AVATAR_ACCESSORIES,
  vibeIcon: AVATAR_VIBE_ICONS,
  profilePin: AVATAR_PROFILE_PINS,
};

export const AVATAR_FIELDS = Object.keys(FIELD_OPTIONS) as AvatarField[];

export function avatarFieldOptions<K extends AvatarField>(
  field: K,
): readonly UserAvatarConfig[K][] {
  return FIELD_OPTIONS[field];
}

export const DEFAULT_AVATAR_CONFIG: UserAvatarConfig = {
  version: AVATAR_VERSION,
  skinTone: "neutral_yellow",
  faceShape: "round",
  eyeStyle: "almond",
  eyeColor: "brown",
  browStyle: "soft",
  noseStyle: "soft",
  hairStyle: "none",
  hairColor: "chestnut_brown",
  facialHair: "none",
  glasses: "none",
  headCovering: "none",
  expression: "neutral",
  topStyle: "tee",
  topColor: "blue",
  accessory: "none",
  vibeIcon: "none",
  profilePin: "none",
};

export function avatarSeedHash(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Deterministically derive a complete avatar from a safe seed (userId / avatar
 * seed — never email, phone, or DOB). Stable across launches, no write needed.
 */
export function generateAvatarFromSeed(seed: string): UserAvatarConfig {
  const base = avatarSeedHash(seed || "opncue");
  const pick = <T,>(options: readonly T[], salt: number): T =>
    options[(base + salt * 2654435761) % options.length];

  return {
    version: AVATAR_VERSION,
    skinTone: pick(AVATAR_SKIN_TONES, 1),
    faceShape: pick(AVATAR_FACE_SHAPES, 2),
    eyeStyle: pick(AVATAR_EYE_STYLES, 9),
    eyeColor: pick(AVATAR_EYE_COLORS, 10),
    browStyle: pick(AVATAR_BROW_STYLES, 11),
    noseStyle: pick(AVATAR_NOSE_STYLES, 12),
    hairStyle: pick(AVATAR_HAIR_STYLES.filter((s) => s !== "none"), 3),
    hairColor: pick(AVATAR_HAIR_COLORS.slice(0, 8), 4),
    facialHair: "none",
    glasses:
      base % 4 === 0 ? pick(AVATAR_GLASSES.filter((g) => g !== "none"), 5) : "none",
    headCovering: "none",
    expression: pick(AVATAR_EXPRESSIONS, 6),
    topStyle: pick(AVATAR_TOP_STYLES, 7),
    topColor: pick(AVATAR_COLORS, 8),
    accessory: "none",
    vibeIcon: "none",
    // Never generated: a pin is a disclosure the user makes, not a look
    // the app hands out. See AVATAR_PROFILE_PINS.
    profilePin: "none",
  };
}

/** Uniformly random complete config (for the editor's "Randomize"). */
export function randomAvatarConfig(): UserAvatarConfig {
  return generateAvatarFromSeed(
    `rnd-${Math.random().toString(36).slice(2)}-${Date.now()}`,
  );
}

/**
 * Coerce arbitrary/partial/legacy input into a complete, valid config. Unknown
 * fields dropped, invalid values fall back to the neutral default. Never
 * throws — safe for rendering untrusted or version-mismatched data.
 */
export function normalizeAvatarConfig(
  input: unknown,
  seed?: string,
): UserAvatarConfig {
  const fallback = DEFAULT_AVATAR_CONFIG;
  if (!input || typeof input !== "object") return fallback;

  const raw = input as Record<string, unknown>;
  const result: UserAvatarConfig = { ...fallback, version: AVATAR_VERSION };

  for (const field of AVATAR_FIELDS) {
    const value = raw[field];
    const options = FIELD_OPTIONS[field] as readonly string[];
    if (typeof value === "string" && options.includes(value)) {
      (result as Record<string, unknown>)[field] = value;
    } else if (field === "hairStyle" && typeof value === "string") {
      result.hairStyle = LEGACY_HAIR_STYLE_ALIASES[value] ?? result.hairStyle;
    }
  }

  return result;
}

/**
 * Resolve the config to render: a saved config (normalized), else a
 * neutral default. Always returns a complete config.
 */
export function resolveAvatarConfig(
  config: UserAvatarConfig | null | undefined,
  seed: string | undefined,
): UserAvatarConfig {
  if (config) return normalizeAvatarConfig(config, seed);
  return DEFAULT_AVATAR_CONFIG;
}

// ---- Render palettes (illustration only, brand-aligned) ----

export const AVATAR_SKIN_HEX: Record<AvatarSkinTone, string> = {
  neutral_yellow: "#F8C84E",
  tone_1: "#F8DAC0",
  tone_2: "#EFC49E",
  tone_3: "#E0A87A",
  tone_4: "#CC8C5A",
  tone_5: "#B0703F",
  tone_6: "#8C5732",
  tone_7: "#6B4226",
  tone_8: "#4A2C1A",
};

export const AVATAR_EYE_HEX: Record<AvatarEyeColor, string> = {
  brown: "#5B3A21",
  dark_brown: "#3B2416",
  amber: "#B06A1E",
  hazel: "#7A5A2E",
  green: "#3E7D52",
  blue: "#3A6FB0",
  gray: "#6E7B86",
  violet: "#7C5AC2",
};

export const AVATAR_HAIR_HEX: Record<AvatarHairColor, string> = {
  jet_black: "#1A1A1A",
  dark_espresso: "#30221A",
  chestnut_brown: "#593E2B",
  honey_blonde: "#D1A14E",
  deep_auburn: "#6E3320",
  classic_ginger: "#A8411E",
  silver_gray: "#9097A1",
  off_white: "#EAEEF3",
  electric_blue: "#2F6BBF",
  hot_pink: "#DE649A",
  vibrant_violet: "#764BBF",
  sandy_blonde: "#BFA076",
  copper_red: "#C95A34",
  salt_pepper: "#5A616B",
  mint_green: "#78C2A4",
  soft_lavender: "#B19CD9",
  rose_gold: "#E3A39A",
  teal_blue: "#1F8A94",
};

/** Theme-safe top colors, aligned to the opncue blue/navy palette. */
export const AVATAR_TOP_HEX: Record<AvatarColor, string> = {
  blue: "#007BFF",
  navy: "#003E80",
  sky: "#4E9DFF",
  teal: "#14B8A6",
  green: "#22A06B",
  amber: "#E0A800",
  coral: "#F97316",
  pink: "#EC4899",
  purple: "#6C5CE7",
  slate: "#5A6675",
};
